<template>
  <aha-modal
    @hide="hide()"
    :title="$i18nPresenter.t('Copy to another workspace')"
  >
    <div class="mt-2 overflow-auto max-h-36">
      {{ $i18nPresenter.t("Copy the presentation") }}<b>“{{ presentation.name }}”</b>{{ $i18nPresenter.t("to:") }}
      <div class="list-workspace">
        <div
          v-if="!isInPersonalMode"
          class="flex flex-row items-center mt-4"
        >
          <aha-radio
            name="copyTo"
            :value="'personal'"
            v-model="copyTo"
            @change="onChangeCopyDestination"
          >
          </aha-radio>
          <div>
            <aha-avatar
              :name="currentUser.firstName"
              :id="currentUser.id"
              :width="36"
              :height="36"
              class="mr-2 text-lg"
            />
          </div>
          <div class="flex flex-col max-w-60">
            <div class="inline-flex font-bold">
              {{ $i18nPresenter.t("Personal") }}
            </div>
            <div class="inline-flex text-xs text-base-80">
              {{ currentUser.firstName }}
            </div>
          </div>
        </div>
        <div
          v-if="isInPersonalMode"
          class="flex flex-row items-center mt-4"
        >
          <aha-radio
            name="copyTo"
            :value="currentAccount.id"
            v-model="copyTo"
            @change="onChangeCopyDestination"
          >
          </aha-radio>
          <div>
            <aha-avatar
              :name="currentAccount.name"
              :id="currentAccount.id"
              :width="36"
              :height="36"
              class="mr-2 text-lg"
            />
          </div>
          <div class="flex flex-col max-w-60">
            <div class="inline-flex font-bold">
              {{ currentAccount.name }}
            </div>
            <div class="inline-flex text-xs text-base-80">
              {{ getTeamTotalMembers() }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <aha-checkbox
      v-model="isIncludeResponse"
      class="mr-auto"
    >
      {{ $i18nPresenter.t("Include response data") }}
      <i
        class="ml-2 fas fa-question-circle"
        style="color: #b7b7b7 !important"
        v-tooltip="TOOLTIP_INCLUDE_RESPONSE"
      />
    </aha-checkbox>
    <template #action>
      <aha-link @click="hide()">
        <span class="font-bold">{{ $i18nPresenter.t("Cancel") }}</span>
      </aha-link>
      <aha-button
        :disabled="isCopyToNewWorkspace"
        @click="handleCopyToNewWorkspace()"
        :submitting="isCopyToNewWorkspace"
        has-margin-left
      >
        {{ $i18nPresenter.t("Copy") }}
      </aha-button>
    </template>
  </aha-modal>
</template>
<script>
import { mapActions, mapState } from 'vuex'
import * as Sentry from '@sentry/vue'
import configs from '@/config'
import util from '@/util'
import constants from '@/constant'
import UserApi from '@/api/user'

const { eventCategories } = constants

export default {
  props: {
    presentation: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      isCopyToNewWorkspace: false,
      isIncludeResponse: false,
      copyTo: 'personal',
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
      currentAccount: (state) => state.currentAccount,
    }),
    isInPersonalMode() {
      return this.$route.name === 'Presentations' || this.$route.name === 'SharedWithMe' || this.$route.name === 'FolderDetail'
    },
  },
  created() {
    this.TOOLTIP_INCLUDE_RESPONSE = this.$i18nPresenter.t('Response data is anything submitted by your audience (answers, reactions, Q&A questions, etc.)')
    this.getMyTeam()
  },
  mounted() {
    this.copyTo = this.getCopytoValue()
  },
  destroyed() {
  },
  methods: {
    ...mapActions([
      'setCurrentAccountAction',
    ]),
    hide() {
      util.mixpanelTracking(
        eventCategories.PRESENTATION_LIST,
        'Copy to anorther workspace modal > Cancel',
        null,
        null,
        {
          presentationId: this.presentation.id,
          isTeamPresentations: this.$store.state.workingMode === constants.workspace.TEAM,
        },
      )
      this.$emit('hide')
    },
    getCopytoValue() {
      if (this.$route.name === 'Presentations') return this.$store.state.currentUser.accountId
      return 'personal'
    },
    getTeamTotalMembers() {
      if (!this.$store.state.currentAccount.totalMembers) return ''
      if (this.$store.state.currentAccount.totalMembers == 1) return `1 ${this.$i18nPresenter.t('member')}`
      return `${this.$store.state.currentAccount.totalMembers} ${this.$i18nPresenter.t('members')}`
    },
    async handleCopyToNewWorkspace() {
      try {
        this.isCopyToNewWorkspace = true
        const payload = {
          id: this.presentation.shareCode,
          name: this.presentation.name,
        }
        if (this.isIncludeResponse) payload.isResponseDataIncluding = true
        if (this.copyTo === 'personal') payload.isCopyToPersonalAccount = true
        else {
          payload.accountId = this.copyTo
          payload.isCopyToPersonalAccount = false
        }

        util.mixpanelTracking(
          eventCategories.PRESENTATION_LIST,
          'Copy to anorther workspace modal > Copy',
          null,
          null,
          {
            presentationId: this.presentation.id,
            isTeamPresentations: this.$store.state.workingMode === constants.workspace.TEAM,
          },
        )

        const newCopy = await this.$http.post(configs.api.copyPresentation, payload)
        if (newCopy.status === 200) {
          this.isCopyToNewWorkspace = false
          const message = `${this.$i18nPresenter.t('Successfully copied')} “${util.sanitize(this.presentation.name)}” ${this.$i18nPresenter.t('to the')}
            ${this.copyTo === 'personal' ? this.$i18nPresenter.t('Personal') : util.sanitize(this.currentAccount.name)} ${this.$i18nPresenter.t('workspace')}`
          util.showToastSuccess(message)
          if (this.copyTo === 'personal') this.$router.push({ name: 'Presentations' })
          else this.$router.push({ name: 'Team' })
          util.mixpanelTracking(
            eventCategories.PRESENTATION_LIST,
            'Create new presentation',
            'Copy from another workspace',
            null,
            {
              sourcePresentationId: this.presentation.id,
              presentationId: newCopy.body.id || 0,
            },
          )
        }
      } catch (error) {
        Sentry.captureException(error)
        this.isCopyToNewWorkspace = false
        util.showToastError(this.$i18nPresenter.t('Something error, please try again in a few minutes.'))
      }
    },
    onChangeCopyDestination() {
      util.mixpanelTracking(
        eventCategories.PRESENTATION_LIST,
        'Copy to anorther workspace modal > Select workspace',
        null,
        null,
        {
          presentationId: this.presentation.id,
          isTeamPresentations: this.$store.state.workingMode === constants.workspace.TEAM,
        },
      )
    },
    async getMyTeam() {
      if (!this.$store.state.currentUser.id) {
        return
      }
      // eslint-disable-next-line no-useless-catch
      try {
        const res = await UserApi.getTeamMembers({
          userId: this.$store.state.currentUser.id,
        })
        const { totalUsers } = res
        const account = this.$store.state.currentAccount
        this.setCurrentAccountAction({
          ...account,
          totalMembers: totalUsers,
        })
      } catch (error) {
        throw error
      }
    },
  },
}
</script>
<style scoped lang='scss'>
.list-workspace {
  @apply mt-2 mb-6;
}
</style>
